import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FiArrowLeft, FiStar, FiDollarSign, FiCheckCircle, 
  FiBriefcase, FiMapPin, FiPackage, FiTrendingUp
} from 'react-icons/fi';
import { getTourCompanyStats, recordTourCompanyPayment } from '../services/companies';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import PaymentModal from '../components/Common/PaymentModal';
import toast from 'react-hot-toast';

export default function TourCompanyStats() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    fetchStats();
  }, [id]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await getTourCompanyStats(id);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to fetch tour company stats');
      navigate('/tour-companies');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (paymentData) => {
    try {
      await recordTourCompanyPayment(id, paymentData);
      toast.success('Payment recorded successfully');
      setShowPaymentModal(false);
      fetchStats();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!data) return <div className="p-6 text-center">Tour company not found</div>;

  const { company, stats, finances, recent_orders } = data;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/tour-companies')}
            className="p-2 hover:bg-gray-100 rounded-lg mr-4"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{company.name}</h1>
            {company.contact?.person && (
              <p className="text-gray-500">Contact: {company.contact.person}</p>
            )}
            <p className="text-sm text-gray-500 mt-1">{company.contact?.phone}</p>
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full text-sm ${
          company.status === 'active' ? 'bg-green-100 text-green-800' :
          company.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {company.status?.toUpperCase()}
        </div>
      </div>

      {/* Service Types & Areas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center mb-2">
            <FiPackage className="w-5 h-5 text-primary-500 mr-2" />
            <h3 className="font-semibold text-gray-700">Service Types</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {company.service_types?.map((type, idx) => (
              <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm capitalize">
                {type}
              </span>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center mb-2">
            <FiMapPin className="w-5 h-5 text-primary-500 mr-2" />
            <h3 className="font-semibold text-gray-700">Service Areas</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {company.service_areas?.map((area, idx) => (
              <span key={idx} className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm capitalize">
                {area.replace('_', ' ')}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Bookings</p>
              <p className="text-2xl font-bold text-gray-800">{stats.total_orders}</p>
            </div>
            <FiBriefcase className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Completed</p>
              <p className="text-2xl font-bold text-green-600">{stats.completed_orders}</p>
            </div>
            <FiCheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Revenue</p>
              <p className="text-2xl font-bold text-primary-600">${stats.total_revenue}</p>
            </div>
            <FiDollarSign className="w-8 h-8 text-primary-500" />
          </div>
        </div>
      </div>

      {/* Pricing & Commission */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="font-semibold text-gray-700 mb-2">Pricing</h3>
          <p className="text-lg font-bold text-primary-600">${company.pricing?.base_price || 0} / trip</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="font-semibold text-gray-700 mb-2">Commission</h3>
          <p className="text-lg font-bold text-orange-600">
            {company.pricing?.commission_type === 'percentage' 
              ? `${company.pricing?.commission_value}%` 
              : `$${company.pricing?.commission_value}`}
          </p>
          <p className="text-xs text-gray-500">Payment terms: {company.pricing?.payment_terms || 'Not specified'}</p>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Financial Summary</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Earned</span>
              <span className="font-bold text-green-600">${finances.total_earned}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Paid</span>
              <span className="font-bold text-blue-600">${finances.total_paid}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="text-gray-600 font-semibold">Total Due</span>
              <span className="font-bold text-orange-600 text-lg">${finances.total_due}</span>
            </div>
            <button
              onClick={() => setShowPaymentModal(true)}
              className="w-full mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
            >
              Record Payment
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 lg:col-span-2">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Payment History</h2>
          {finances.payment_history?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Amount</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Period</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Method</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Reference</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {finances.payment_history.map((payment, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-2 text-sm">{new Date(payment.paid_at).toLocaleDateString()}</td>
                      <td className="px-4 py-2 text-sm font-medium">${payment.amount}</td>
                      <td className="px-4 py-2 text-sm">
                        {payment.period_start && payment.period_end ? 
                          `${new Date(payment.period_start).toLocaleDateString()} - ${new Date(payment.period_end).toLocaleDateString()}` : '-'}
                      </td>
                      <td className="px-4 py-2 text-sm capitalize">{payment.method}</td>
                      <td className="px-4 py-2 text-sm">{payment.reference_number || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No payment history</p>
          )}
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Recent Bookings</h2>
        {recent_orders?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Order #</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Amount</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {recent_orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/orders/${order.id}`)}>
                    <td className="px-4 py-2 text-sm font-medium text-primary-600">{order.order_number}</td>
                    <td className="px-4 py-2 text-sm">{new Date(order.date).toLocaleDateString()}</td>
                    <td className="px-4 py-2 text-sm">${order.amount}</td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        order.status === 'completed' ? 'bg-green-100 text-green-800' :
                        order.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No bookings yet</p>
        )}
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onConfirm={handlePayment}
        performerName={company.name}
        performerType="tour company"
        totalDue={finances.total_due}
      />
    </div>
  );
}
